// src/components/kpi/KpiHelpModal.jsx
import React, { useMemo } from "react";
import { Modal } from "react-bootstrap";
import "./KpiHelpModal.css";

const HELP = {
  // Board areas (tiles)
  areas: {
    "Access Management": {
      what: "Access governance for privileged accounts and excessive entitlements.",
      calc:
        "Tile status is the worst RAG among KPIs in this area (Red beats Amber beats Green). Trend is based on the last months status evolution.",
      expected:
        "Focus on privileged access hygiene. If you do not have a reliable monthly figure, leave blank (N/A).",
    },
    "Data Security": {
      what: "Protection of personal data and data classification capabilities.",
      calc:
        "Worst RAG of the KPIs in this area. Trend is based on the last months status evolution.",
      expected:
        "Provide what you can measure reliably. If a KPI is not measurable locally yet, leave blank (N/A).",
    },
    "Incident Management": {
      what: "Incident detection and response performance (volume and time-to-x).",
      calc:
        "Worst RAG of the KPIs in this area. Trend is based on the last months status evolution.",
      expected:
        "Use your incident tracking source. Use whole numbers. Leave blank if not tracked.",
    },
    "Overall Cyber Risk": {
      what: "A simple proxy of compliance against Minimum Security Standards (MSS).",
      calc:
        "This tile reflects MSS compliance KPI(s) configured in this area. Status comes from thresholds (target/amber) configured in kpi_definitions.",
      expected:
        "Enter the monthly compliance percentage (0 to 100). If not available, leave blank.",
    },
    "Patch Management": {
      what: "Patch posture and patch speed.",
      calc:
        "Worst RAG of the KPIs in this area. Trend is based on the last months status evolution.",
      expected:
        "Use your patch tooling numbers (Action1/Intune/WSUS). Keep it consistent month over month.",
    },
    "Security Operations": {
      what: "Operational security posture (testing, remediation backlog, visibility).",
      calc:
        "Worst RAG of the KPIs in this area. Trend is based on the last months status evolution.",
      expected:
        "Use the last known pentest/vuln scan date and the number of open high/critical items.",
    },
    "Third party supplier": {
      what: "Cyber compliance of third party suppliers (TPP).",
      calc:
        "Worst RAG of the KPIs in this area. Trend is based on the last months status evolution.",
      expected:
        "Enter the % of suppliers assessed as compliant vs your standard. Leave blank if not run yet.",
    },
    // Backward compatible labels if your board_area uses slightly different naming
    "Third party supplier management": {
      what: "Cyber compliance of third party suppliers (TPP).",
      calc:
        "Worst RAG of the KPIs in this area. Trend is based on the last months status evolution.",
      expected:
        "Enter the % of suppliers assessed as compliant vs your standard. Leave blank if not run yet.",
    },
    "Overall Cyber Risk Score": {
      what: "A simple proxy of compliance against Minimum Security Standards (MSS).",
      calc:
        "This tile reflects MSS compliance KPI(s) configured in this area. Status comes from thresholds (target/amber) configured in kpi_definitions.",
      expected:
        "Enter the monthly compliance percentage (0 to 100). If not available, leave blank.",
    },
  },

  // KPI keys
  kpis: {
    PRIVILEGED_NONCOMPLIANT: {
      what: "Number of users with admin access or excessive entitlements not aligned with the model.",
      expected:
        "Enter a monthly count (integer). Use your PAM/IAM review or admin group review. Leave blank if not measured.",
      source: "IAM / AD groups review / PAM",
    },

    MSS_COMPLIANCE_PCT: {
      what: "Percentage compliance against Minimum Security Standards (MSS) for the entity.",
      expected:
        "Enter a percentage 0 to 100. Use the same assessment method every month. Leave blank if not assessed.",
      source: "MSS baseline assessment",
    },

    DEVICES_FULLY_PATCHED: {
      what: "Number of devices fully patched at the time of reporting (as per your patch policy).",
      expected:
        "Enter a monthly count (integer). Must use the same scope as your managed device baseline.",
      source: "Action1 / Intune / WSUS / patch tooling",
    },

    PATCH_TIME_DAYS_AVG: {
      what: "Average time to implement security patches once released by vendors.",
      expected:
        "Enter number of days (integer). Use your operational KPI (mean or median), but keep consistent.",
      source: "Patch tooling KPI / change records",
    },

    SECURITY_INCIDENTS: {
      what: "Number of cyber incidents detected in the month (as per your definition).",
      expected:
        "Enter a monthly count (integer). Only include incidents that match the shared definition.",
      source: "SOC / ticketing / incident register",
    },

    MTTD_HOURS: {
      what: "Mean Time To Detect: average time from occurrence to detection.",
      expected:
        "Enter hours (integer). Use your incident tracking timestamps. Leave blank if not tracked.",
      source: "SOC / incident tracking",
    },

    MTTC_HOURS: {
      what: "Mean Time To Contain: average time from detection to containment.",
      expected: "Enter hours (integer). Leave blank if not tracked.",
      source: "SOC / incident tracking",
    },

    MTTR_HOURS: {
      what: "Mean Time To Resolve: average time from detection to closure/resolution.",
      expected: "Enter hours (integer). Leave blank if not tracked.",
      source: "SOC / incident tracking",
    },

    LAST_PENTEST_AGE_DAYS: {
      what: "Age in days since the last penetration test or vulnerability scan.",
      expected:
        "Enter number of days since last run (integer). If never performed, leave blank (do not guess).",
      source: "Pentest reports / vuln scanner history",
    },

    OPEN_HIGH_CRIT_ISSUES: {
      what: "Number of open High/Critical security issues (from pentest/vuln scanning backlog).",
      expected:
        "Enter a count (integer). Only High/Critical. Must be the backlog at month end.",
      source: "Vuln management backlog / remediation tracker",
    },

    UNIDENTIFIED_DEVICES: {
      what: "Number of devices visible in the environment but not managed/identified in the baseline.",
      expected:
        "Enter a count (integer). If you do not have discovery tooling yet, leave blank.",
      source: "Discovery / CMDB / network inventory",
    },

    TRAINING_COMPLIANCE_PCT: {
      what: "Percentage of staff completing required security awareness training.",
      expected:
        "Enter % 0 to 100. Define the eligible population clearly (scope must be stable).",
      source: "LMS / awareness platform",
    },

    APPS_CLASSIFICATION_PCT: {
      what: "Percentage of business applications with integrated data classification functionality.",
      expected:
        "Enter % 0 to 100. If not applicable because you do not have an app inventory, leave blank.",
      source: "App inventory / data classification program",
    },

    PII_ENCRYPTION_PCT: {
      what: "Percentage of personally identifiable records encrypted at rest and in transit (where applicable).",
      expected:
        "Enter % 0 to 100. Use a defensible method (scope, systems). If unknown, leave blank.",
      source: "Data security controls / architecture review",
    },

    PII_RECORDS_COUNT: {
      what: "Number of personally identifiable records stored by or on behalf of the organisation.",
      expected:
        "Enter a count (integer) only if you truly track it. Otherwise leave blank (do not estimate).",
      source: "Data inventory / DPO records / systems of record",
    },

    TPP_COMPLIANCE_PCT: {
      what: "Percentage of third party suppliers compliant with your cyber security standards (TPP).",
      expected:
        "Enter % 0 to 100. Based on assessed suppliers only (define denominator). Leave blank if not run.",
      source: "Third party risk management process",
    },
  },
};

function kindLabel(kindRaw) {
  const k = String(kindRaw || "").toLowerCase();
  if (k === "pct") return "%";
  if (k === "days") return "days";
  if (k === "hours") return "hours";
  if (k === "int") return "count";
  if (k === "score") return "score";
  return k || "number";
}

function fmtThreshold(v, kindRaw) {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);

  const k = String(kindRaw || "").toLowerCase();
  if (k === "pct") return `${String(parseFloat(n.toFixed(1)))}%`;
  if (k === "int" || k === "days" || k === "hours") return String(parseInt(n, 10));
  if (k === "score") return String(parseFloat(n.toFixed(0)));
  return String(parseFloat(n.toFixed(2)));
}

function ragRuleText(d) {
  const hib = Number(d?.higher_is_better ?? 1) === 1;
  const target = d?.target;
  const amber = d?.amber_threshold;

  if (
    target === null ||
    target === undefined ||
    target === "" ||
    amber === null ||
    amber === undefined ||
    amber === ""
  ) {
    return "RAG: not configured";
  }

  const t = fmtThreshold(target, d?.value_kind);
  const a = fmtThreshold(amber, d?.value_kind);

  if (hib) return `RAG: Green if >= ${t}, Amber if >= ${a}, Red if < ${a}`;
  return `RAG: Green if <= ${t}, Amber if <= ${a}, Red if > ${a}`;
}

const AREA_LABELS = {
  "OVERALL CYBER RISK SCORE": "OVERALL CYBER RISK",
  "THIRD PARTY SUPPLIER MANAGEMENT": "THIRD PARTY SUPPLIER",
};

function displayAreaName(area) {
  const up = String(area || "").trim().toUpperCase();
  return AREA_LABELS[up] || String(area || "");
}

export default function KpiHelpModal({ show, onHide, entity, period, rows = [] }) {
  const { kpis, areas } = useMemo(() => {
    const list = Array.isArray(rows) ? rows.slice() : [];

    const kpiList = list
      .map((r) => ({
        kpi_key: r?.kpi_key,
        label: r?.board_kpi_label || r?.label || r?.kpi_key,
        board_area: r?.board_area || r?.category || "KPIs",
        value_kind: r?.value_kind,
        higher_is_better: r?.higher_is_better,
        target: r?.target,
        amber_threshold: r?.amber_threshold,
        red_threshold: r?.red_threshold,
        sort: Number(r?.board_sort ?? r?.sort_order ?? 0),
      }))
      .filter((x) => !!x.kpi_key)
      .sort((a, b) => {
        const aa = String(a.board_area).localeCompare(String(b.board_area));
        if (aa !== 0) return aa;
        return a.sort - b.sort;
      });

    const map = new Map();
    for (const d of kpiList) {
      const area = String(d.board_area || "KPIs");
      if (!map.has(area)) map.set(area, []);
      map.get(area).push(d);
    }

    const areaList = Array.from(map.entries())
      .map(([area, items]) => ({
        board_area: area,
        kpis: items.sort((a, b) => a.sort - b.sort),
      }))
      .sort((a, b) => String(a.board_area).localeCompare(String(b.board_area)));

    return { kpis: kpiList, areas: areaList };
  }, [rows]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      backdrop="static"
      dialogClassName="kpi-help-modal"
      contentClassName="kpi-help-modal-content"
    >
      <Modal.Header closeButton className="kpi-help-modal-header">
        <Modal.Title className="kpi-help-modal-title">Help</Modal.Title>
      </Modal.Header>

      <Modal.Body className="kpi-help-modal-body">
        <div className="kpi-help-meta">
          Entity: <strong>{String(entity || "").toUpperCase()}</strong> | Period:{" "}
          <strong>{String(period || "")}</strong>
        </div>

        <div className="kpi-help-section">
          <div className="kpi-help-section-title">Trend tiles</div>
          <div className="kpi-help-section-sub">
            Each tile is a board area. Status is the worst KPI RAG in that area. If a KPI has no thresholds, it may show as N/A.
          </div>

          {!areas.length ? (
            <div className="kpi-help-empty">No board areas configured.</div>
          ) : (
            areas.map((a) => {
              const rawArea = String(a.board_area || "KPIs");
              const display = displayAreaName(rawArea);
              const areaHelp = HELP.areas[rawArea] || HELP.areas[display] || null;

              return (
                <div key={`area-${rawArea}`} className="kpi-help-card">
                  <div className="kpi-help-card-title">{display}</div>

                  {areaHelp ? (
                    <div className="kpi-help-card-sub">
                      <div><strong>Meaning:</strong> {areaHelp.what}</div>
                      <div><strong>How it is calculated:</strong> {areaHelp.calc}</div>
                      <div><strong>Expected from countries:</strong> {areaHelp.expected}</div>
                    </div>
                  ) : (
                    <div className="kpi-help-card-sub">
                      Meaning and calculation are not documented yet for this area.
                    </div>
                  )}

                  <div className="kpi-help-list">
                    {a.kpis.map((d) => {
                      const kHelp = HELP.kpis[d.kpi_key] || null;
                      return (
                        <div key={`ak-${d.kpi_key}`} className="kpi-help-list-row">
                          <div className="kpi-help-kpi-name">{d.label}</div>
                          <div className="kpi-help-kpi-meta">
                            <div><strong>What it is:</strong> {kHelp?.what || "Not documented yet."}</div>
                            <div><strong>Expected:</strong> {kHelp?.expected || "Provide a reliable monthly value or leave blank (N/A)."}</div>
                            <div><strong>Unit:</strong> {kindLabel(d.value_kind)}</div>
                            <div><strong>Typical source:</strong> {kHelp?.source || "Local process/tooling"}</div>
                            <div><strong>{ragRuleText(d)}</strong></div>
                            <div className="kpi-help-kpi-key">Key: {d.kpi_key}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="kpi-help-section">
          <div className="kpi-help-section-title">KPI list</div>
          <div className="kpi-help-section-sub">
            Full list of KPIs you can fill monthly. If you cannot measure a KPI reliably yet, leave it blank (it will appear as N/A).
          </div>

          {!kpis.length ? (
            <div className="kpi-help-empty">No KPI definitions found.</div>
          ) : (
            <div className="kpi-help-list">
              {kpis.map((d) => {
                const kHelp = HELP.kpis[d.kpi_key] || null;
                return (
                  <div key={`k-${d.kpi_key}`} className="kpi-help-list-row">
                    <div className="kpi-help-kpi-name">{d.label}</div>
                    <div className="kpi-help-kpi-meta">
                      <div><strong>Area:</strong> {displayAreaName(String(d.board_area))}</div>
                      <div><strong>What it is:</strong> {kHelp?.what || "Not documented yet."}</div>
                      <div><strong>Expected:</strong> {kHelp?.expected || "Provide a reliable monthly value or leave blank (N/A)."}</div>
                      <div><strong>Unit:</strong> {kindLabel(d.value_kind)}</div>
                      <div><strong>Typical source:</strong> {kHelp?.source || "Local process/tooling"}</div>
                      <div><strong>{ragRuleText(d)}</strong></div>
                      <div className="kpi-help-kpi-key">Key: {d.kpi_key}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer className="kpi-help-modal-footer">
        <button type="button" className="kpi-btn" onClick={onHide}>
          Close
        </button>
      </Modal.Footer>
    </Modal>
  );
}